import React, { useState, useEffect } from 'react';
import { Register } from './Register';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageProvider';
import { ActivationForm } from './ActivationForm';
import { QualizLogo } from './QualizLogo';
import { API_URL } from '../config/api';
import { api } from '../services/api';

export function SimpleAuth({ children, onLogin, user, onLogout }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [activationToken, setActivationToken] = useState('');
  const [activationNotice, setActivationNotice] = useState('');
  const { t } = useTranslation();
  const { language, setLanguage, availableLanguages } = useLanguage();
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [appVersion, setAppVersion] = useState('0.1.0');

  useEffect(() => {
    document.title = t('auth.loginPageTitle');
  }, [t]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    const isActivationPath = window.location.pathname.toLowerCase().includes('/activate');

    if (tokenParam && isActivationPath) {
      setActivationToken(tokenParam);
    }
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await api.getPublicSystemSettings();
        if (response.success && response.settings) {
          setRegistrationEnabled(
            response.settings.allowUserRegistration !== undefined
              ? response.settings.allowUserRegistration
              : true
          );
        }
      } catch (error) {
        console.warn(
          'Unable to load public system settings, defaulting to registration enabled.',
          error
        );
      }
    };
    loadSettings();

    // Load version from version.json
    const loadVersion = async () => {
      try {
        const response = await fetch('/version.json');
        const data = await response.json();
        if (data.version) {
          setAppVersion(data.version);
        }
      } catch (error) {
        console.warn('Unable to load version, using default');
      }
    };
    loadVersion();
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Direct fetch to avoid handleResponse redirect on 401
      const response = await fetch(`${API_URL}/users/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store token and user data
        const userData = {
          ...data.user,
          token: data.token,
        };

        console.log('User logged in:', userData);
        onLogin(userData);

        // Check if there's a redirect path from before logout
        const redirectPath = sessionStorage.getItem('redirect_after_login');
        if (redirectPath) {
          sessionStorage.removeItem('redirect_after_login');
          // Small delay to ensure user state is set
          setTimeout(() => {
            window.location.href = redirectPath;
          }, 100);
        }
      } else {
        // Set appropriate error message based on response
        const errorMessage = data.message || t('auth.loginErrorGeneric');
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(t('auth.connectionError'));
    } finally {
      setLoading(false);
    }
  };

  if (activationToken) {
    return (
      <ActivationForm
        token={activationToken}
        onComplete={() => {
          setActivationNotice('Password set successfully. You can now sign in.');
          setActivationToken('');
          if (window?.history?.replaceState) {
            window.history.replaceState({}, document.title, '/');
          }
        }}
      />
    );
  }

  if (user) {
    return <div data-testid="simple-auth-user-view">{children}</div>;
  }

  if (showRegister) {
    if (!registrationEnabled) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
          <div className="bg-white shadow-lg rounded-xl p-6 max-w-md text-center space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">Registration Disabled</h2>
            <p className="text-sm text-gray-600">
              New account registration is currently turned off by the system administrator.
            </p>
            <button
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
              onClick={() => setShowRegister(false)}
            >
              Back to Sign In
            </button>
          </div>
        </div>
      );
    }
    return (
      <Register
        onRegister={onLogin}
        onBack={() => setShowRegister(false)}
        registrationEnabled={registrationEnabled}
      />
    );
  }

  return (
    <div
      data-testid="simple-auth-login-view"
      className="min-h-screen bg-gradient-to-br from-blue-500 to-white-600 flex items-center justify-center p-4"
    >
      <div
        data-testid="simple-auth-login-container"
        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
      >
        <div
          data-testid="simple-auth-login-header"
          className="text-center mb-8 flex flex-col items-center gap-3"
        >
          <QualizLogo size={72} />
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{t('brand.name')}</h2>
            <p data-testid="simple-auth-p-subtitle" className="text-gray-600">
              {t('brand.tagline')}
            </p>
          </div>
        </div>

        {error && (
          <div
            data-testid="simple-auth-div-error"
            className="mb-4 p-4 bg-red-50 border border-red-300 rounded-lg"
          >
            <div data-testid="simple-auth-div-error-flex" className="flex items-start">
              <svg
                className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div data-testid="simple-auth-div-error-text-container" className="flex-1">
                <h3
                  data-testid="simple-auth-h3-error-title"
                  className="text-sm font-semibold text-red-800 mb-1"
                >
                  {t('auth.loginFailedTitle')}
                </h3>
                <p data-testid="simple-auth-p-error-message" className="text-sm text-red-700 mb-2">
                  {error}
                </p>
                <p data-testid="simple-auth-p-error-instructions" className="text-xs text-red-600">
                  {t('auth.loginFailedInstructions')}
                </p>
              </div>
            </div>
          </div>
        )}

        {activationNotice && !error && (
          <div className="mb-4 p-4 bg-green-50 border border-green-300 rounded-lg text-sm text-green-800">
            {activationNotice}
          </div>
        )}

        <form data-testid="simple-auth-form" onSubmit={handleSubmit} className="space-y-4">
          <div data-testid="simple-auth-div-email">
            <label
              data-testid="simple-auth-label-email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t('auth.emailLabel')}
            </label>
            <input
              data-testid="simple-auth-input-email"
              type="email"
              value={email}
              onChange={e => {
                setEmail(e.target.value);
                if (error) setError(''); // Clear error when user starts typing
              }}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                error
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-indigo-500'
              }`}
              placeholder={t('auth.placeholderEmail')}
              required
              disabled={loading}
            />
          </div>

          <div data-testid="simple-auth-div-password">
            <label
              data-testid="simple-auth-label-password"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t('auth.passwordLabel')}
            </label>
            <input
              data-testid="simple-auth-input-password"
              type="password"
              value={password}
              onChange={e => {
                setPassword(e.target.value);
                if (error) setError(''); // Clear error when user starts typing
              }}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
                error
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-indigo-500'
              }`}
              placeholder={t('auth.placeholderPassword')}
              required
              disabled={loading}
            />
          </div>

          <div data-testid="simple-auth-language-select" className="pt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('language.label')}
            </label>
            <div className="flex items-center gap-3">
              <select
                data-testid="simple-auth-select-language"
                value={language}
                onChange={e => setLanguage(e.target.value)}
                className="w-48 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {availableLanguages.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500">{t('language.helper')}</span>
            </div>
          </div>

          <button
            data-testid="simple-auth-button-sign-in"
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-500 transition font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? t('auth.signingIn') : t('auth.signIn')}
          </button>
        </form>

        {registrationEnabled && (
          <div data-testid="simple-auth-div-register-link" className="mt-4 text-center">
            <button
              data-testid="simple-auth-button-register"
              onClick={() => setShowRegister(true)}
              className="text-blue-600 hover:text-blue-500 text-sm font-medium"
            >
              {t('auth.registerCta')}
            </button>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">{t('brand.version', { version: appVersion })}</p>
        </div>
      </div>
    </div>
  );
}


export default SimpleAuth;